"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Range = void 0;
const compare_1 = require("@pallad/compare");
const errors_1 = require("./errors");
const toTupleMapper = {
    start({ start }) {
        return [start];
    },
    end({ end }) {
        return [undefined, end];
    },
    full({ start, end }) {
        return [start, end];
    }
};
var Range;
(function (Range) {
    let Full;
    (function (Full) {
        function is(value) {
            return typeof value === 'object' && value !== null && 'start' in value && 'end' in value;
        }
        Full.is = is;
    })(Full = Range.Full || (Range.Full = {}));
    let Start;
    (function (Start) {
        function is(value) {
            return typeof value === 'object' && value !== null && 'start' in value;
        }
        Start.is = is;
    })(Start = Range.Start || (Range.Start = {}));
    let End;
    (function (End) {
        function is(value) {
            return typeof value === 'object' && value !== null && 'end' in value;
        }
        End.is = is;
    })(End = Range.End || (Range.End = {}));
    Range.isStart = Start.is;
    Range.isEnd = End.is;
    Range.isFull = Full.is;
    function is(value) {
        return Full.is(value) || Start.is(value) || End.is(value);
    }
    Range.is = is;
    function create(start, end, comparator) {
        if (start !== undefined && start !== null && end !== undefined && end !== null) {
            if ((0, compare_1.compare)(start, end, comparator).isGreater) {
                throw errors_1.ERRORS.START_GREATER_THAN_END.create();
            }
            return { start, end };
        }
        else if (start !== undefined && start !== null) {
            return { start };
        }
        else if (end !== undefined && end !== null) {
            return { end };
        }
        throw errors_1.ERRORS.UNDEFINED_BOUNDARIES.create();
    }
    Range.create = create;
    function toTuple(range) {
        return Range.map(range, toTupleMapper);
    }
    Range.toTuple = toTuple;
    function fromArray(arr, comparator) {
        if (arr.length === 0) {
            throw errors_1.ERRORS.EMPTY_ARRAY_ARGUMENT.create();
        }
        return create(arr[0], arr[1], comparator);
    }
    Range.fromArray = fromArray;
    /**
     * Maps range using provided mapper
     *
     * Mapper defines set of functions used for mapping Start, End and Full range.
     */
    function map(range, mapper) {
        if (Range.Full.is(range)) {
            return callMapping(mapper.full, range);
        }
        else if (Range.Start.is(range)) {
            return callMapping(mapper.start, range);
        }
        return callMapping(mapper.end, range);
    }
    Range.map = map;
    /**
     * Checks whether given value falls in range
     *
     * `exclusive` param describes whether to perform exclusive check - by default false
     */
    function isWithin(range, value, exclusive, comparator) {
        var _a, _b;
        const [isStartExclusive, isEndExclusive] = typeof exclusive === 'object' ?
            [(_a = exclusive.start) !== null && _a !== void 0 ? _a : false, (_b = exclusive.end) !== null && _b !== void 0 ? _b : false] :
            [exclusive, exclusive];
        return Range.map(range, {
            full({ start, end }) {
                return (0, compare_1.compare)(value, start, comparator)[isStartExclusive ? 'isGreater' : 'isGreaterOrEqual'] &&
                    (0, compare_1.compare)(value, end, comparator)[isEndExclusive ? 'isLess' : 'isLessOrEqual'];
            },
            start({ start }) {
                return (0, compare_1.compare)(value, start, comparator)[isStartExclusive ? 'isGreater' : 'isGreaterOrEqual'];
            },
            end({ end }) {
                return (0, compare_1.compare)(value, end, comparator)[isEndExclusive ? 'isLess' : 'isLessOrEqual'];
            }
        });
    }
    Range.isWithin = isWithin;
    /**
     * Converts given range to another range using mapper on each value.
     *
     * Convenience method over `Range.map`
     */
    function convert(range, mapper, comparator) {
        return Range.map(range, {
            full({ start, end }) {
                return create(mapper(start), mapper(end), comparator);
            },
            start({ start }) {
                return create(mapper(start), undefined, comparator);
            },
            end({ end }) {
                return create(undefined, mapper(end), comparator);
            }
        });
    }
    Range.convert = convert;
})(Range || (exports.Range = Range = {}));
function callMapping(mapping, range) {
    if (typeof mapping === 'function') {
        return mapping(range);
    }
    return mapping;
}
